/**
 * Summary cards for a project's scenarios. The "Rank in cluster" card
 * highlights the project's PERCENTILE rank (P1..P100) rather than the
 * raw rank number, so the value is comparable across clusters of
 * different sizes.
 */
import { IconNames } from "@blueprintjs/icons";
import { RichMetricCard } from "in_queue/components/common/MetricCard";
import { ScenarioMetricsMapping } from "in_queue/types/scenarioMetricsType";
import { formatCosts } from "in_queue/utils/formatting";
import { mean } from "lodash";
import { Loading, mapLoadingState } from "types/loadingType";
import css from "./ProjectMetricCards.module.scss";

interface ProjectMetricsProps {
    /**
     * Narrowed by the caller to the scenarios on screen, so every card
     * moves with the stage tab and the archived and latest-models
     * toggles.
     */
    scenarioMetrics: Loading<ScenarioMetricsMapping>;
}

interface ProjectMetrics {
    medianCostPerMw: number | undefined;
    p25CostPerMw: number | undefined;
    p75CostPerMw: number | undefined;
    avgUpgrades: number | undefined;
    avgRankPercentile: number | undefined;
    minRankPercentile: number | undefined;
    maxRankPercentile: number | undefined;
}

const median = (sorted: ReadonlyArray<number>): number | undefined => {
    if (sorted.length === 0) return undefined;
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) return (sorted[mid - 1] + sorted[mid]) / 2;
    return sorted[mid];
};

const percentileValue = (
    sorted: ReadonlyArray<number>,
    p: number
): number | undefined => {
    if (sorted.length === 0) return undefined;
    const idx = Math.max(
        0,
        Math.min(sorted.length - 1, Math.floor((p / 100) * (sorted.length - 1)))
    );
    return sorted[idx];
};

export const ProjectMetricCards: React.FC<ProjectMetricsProps> = ({
    scenarioMetrics
}) => {
    const projectMetrics: Loading<ProjectMetrics> = mapLoadingState(
        scenarioMetrics,
        (metrics) => {
            const entries = Object.entries(metrics);
            const sortedCostsPerMw = entries
                .map(([, m]) => m.costPerMw)
                .filter((v): v is number => v !== undefined && v >= 0)
                .sort((a, b) => a - b);
            const upgradeCounts = entries
                .map(([, m]) => m.numAssignedUpgrades)
                .filter((v): v is number => v !== undefined);
            const rankPercentiles = entries
                .map(([, m]) => m.rankPercentile)
                .filter((v): v is number => v !== undefined);
            const hasRanks = rankPercentiles.length > 0;
            return {
                medianCostPerMw: median(sortedCostsPerMw),
                p25CostPerMw: percentileValue(sortedCostsPerMw, 25),
                p75CostPerMw: percentileValue(sortedCostsPerMw, 75),
                avgUpgrades:
                    upgradeCounts.length > 0 ? mean(upgradeCounts) : undefined,
                avgRankPercentile: hasRanks ? mean(rankPercentiles) : undefined,
                minRankPercentile: hasRanks
                    ? Math.min(...rankPercentiles)
                    : undefined,
                maxRankPercentile: hasRanks
                    ? Math.max(...rankPercentiles)
                    : undefined
            };
        }
    );

    const rankValue: Loading<string> = mapLoadingState(projectMetrics, (m) =>
        m.avgRankPercentile !== undefined
            ? `p${Math.round(m.avgRankPercentile)}`
            : "-"
    );
    const rankDescription: string =
        projectMetrics !== "loading" &&
        projectMetrics.avgRankPercentile === undefined
            ? "No ranked scenarios yet."
            : projectMetrics !== "loading" &&
              projectMetrics.minRankPercentile !==
                  projectMetrics.maxRankPercentile
            ? `Ranges p${projectMetrics.minRankPercentile} to p${projectMetrics.maxRankPercentile} across scenarios.`
            : "Against every project in the scenario. p1 is cheapest.";

    const costDescription: string =
        projectMetrics !== "loading" &&
        projectMetrics.p25CostPerMw !== undefined &&
        projectMetrics.p75CostPerMw !== undefined
            ? `Middle 50% between ${formatCosts(
                  projectMetrics.p25CostPerMw
              )} and ${formatCosts(projectMetrics.p75CostPerMw)}.`
            : "Allocated cost per MW across this project's scenarios.";

    return (
        <div className={css["ProjectMetricCards"]}>
            <RichMetricCard
                icon={IconNames.WRENCH}
                label="Average number of upgrades"
                value={mapLoadingState(projectMetrics, (m) =>
                    m.avgUpgrades !== undefined
                        ? Math.round(m.avgUpgrades).toString()
                        : "-"
                )}
                valueSuffix="per scenario"
                description="Only includes upgrades carrying an allocated cost."
                tooltip="Average number of network upgrades with assigned cost for this project across its completed scenarios. Higher = more constrained POI. Constraints with $0 allocated cost (e.g. contingent upgrades) are not counted."
            />
            <RichMetricCard
                icon={IconNames.DOLLAR}
                label="Median $/MW"
                value={mapLoadingState(projectMetrics, (m) =>
                    m.medianCostPerMw !== undefined
                        ? formatCosts(m.medianCostPerMw)
                        : "-"
                )}
                description={costDescription}
                tooltip="Median allocated cost per MW across completed scenarios."
            />
            <RichMetricCard
                icon={IconNames.SORT_ASC}
                label="Avg. rank in cluster"
                value={rankValue}
                description={rankDescription}
                tooltip="Average percentile rank by $/MW across this project's scenarios, vs all projects in each scenario. P1 = cheapest, P100 = most expensive."
            />
        </div>
    );
};
